// src/shared/hooks/useRefIdConflict.ts

import { useMemo } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useVideos } from '@/features/media/useVideos';
import { useStreams } from '@/features/streams/useStreams';
import { usePlaylists } from '@/features/playlists/usePlaylists';
import { REF_ID_PATTERN } from '@/lib/constants/media';
import {
  RefIdConflictFinder,
  type RefIdOwner,
  type RefIdScope,
} from '@/shared/utils/refIdConflict';

export interface UseRefIdConflictOptions {
  /** Live form value of the Reference ID field. */
  refId: string;
  /** The asset's saved refId ('' when none) — no check while unchanged. */
  originalRefId: string;
  selfScope: RefIdScope;
  /** mediaId (videos/streams) or listId (playlists). */
  selfId: number;
}

export interface UseRefIdConflictResult {
  conflict: RefIdOwner | null;
  conflictMessage: string | null;
  /** True while list data is still loading for an active check. */
  isChecking: boolean;
}

const DEBOUNCE_MS = 300;

function needsCheck(value: string, originalRefId: string): boolean {
  return value !== '' && value !== originalRefId && REF_ID_PATTERN.test(value);
}

/**
 * Client-side reference-ID uniqueness check across the customer's videos,
 * live streams, and playlists. A UX layer only — the backend refIdChanged
 * validation stays authoritative (a stale cache can miss a just-claimed id).
 *
 * The three list queries are enabled lazily: nothing is fetched until the
 * user actually changes the field to a new, format-valid value, so untouched
 * edit pages and saves cost zero extra requests (large libraries: 33k+ items).
 */
export function useRefIdConflict({
  refId,
  originalRefId,
  selfScope,
  selfId,
}: UseRefIdConflictOptions): UseRefIdConflictResult {
  const [debouncedRefId] = useDebouncedValue(refId, DEBOUNCE_MS);

  // Start fetching on the first keystroke; stay enabled while the debounced
  // value still needs checking so results don't flicker away mid-typing.
  const enabled = needsCheck(refId, originalRefId) || needsCheck(debouncedRefId, originalRefId);

  const { videos, isLoading: videosLoading } = useVideos({ enabled, refetchOnMount: false });
  const { streams, isLoading: streamsLoading } = useStreams({ enabled, refetchOnMount: false });
  const { data: playlistData, isLoading: playlistsLoading } = usePlaylists({ enabled });

  const playlists = useMemo(
    () => (playlistData ? [...playlistData.customLists, ...playlistData.tagLists] : []),
    [playlistData]
  );

  // Rebuilt only when list data identity changes (once per fetch).
  const index = useMemo(
    () => RefIdConflictFinder.buildIndex({ videos, streams, playlists }),
    [videos, streams, playlists]
  );

  const checkActive = needsCheck(debouncedRefId, originalRefId);
  const conflict = checkActive
    ? RefIdConflictFinder.findConflict(index, debouncedRefId, selfScope, selfId)
    : null;

  return {
    conflict,
    conflictMessage: conflict ? RefIdConflictFinder.conflictMessage(conflict) : null,
    isChecking: checkActive && (videosLoading || streamsLoading || playlistsLoading),
  };
}
